/**
 * Unified Authentication Hook - Firebase Auth
 *
 * All authentication is now handled by Firebase Auth with multi-tenant support.
 * This is the single source of truth for authentication state.
 *
 * Migration Note: Stack Auth has been removed. Firebase Auth provides:
 * - Multi-tenant support via custom claims
 * - Multiple auth providers (Google, Email, etc.)
 * - Role-based access control from database RBAC
 *
 * All roles and permissions come from the database RBAC system.
 * Email whitelist has been removed - use database for super admin management.
 */

import { createContext, useContext } from 'react';
import { useFirebaseAuth } from './useFirebaseAuth';
import {
  SUPER_ADMIN_ROUTES,
  isSuperAdminRoute,
} from '../config/auth.config';

/**
 * Shared auth state.
 *
 * useFirebaseAuth() holds its own useState and registers its own
 * onAuthStateChanged subscription, so calling it from every consumer meant ~29
 * duplicate Firebase listeners, 29 independent `loading` flags resolving at
 * different moments, and a separate getIdToken() round trip per component.
 * Auth-dependent UI could therefore disagree with itself mid-render.
 *
 * AuthProvider now calls it exactly once and shares the result.
 */
const AuthContext = createContext(null);

// Admin routes that require super_admin role (use centralized config)
const ADMIN_ROUTES = SUPER_ADMIN_ROUTES;

/**
 * Check if a route requires admin access
 */
const isAdminRoute = (pathname) => {
  return ADMIN_ROUTES.some(route => {
    if (route.endsWith('/*')) {
      return pathname.startsWith(route.replace('/*', ''));
    }
    return pathname === route || pathname.startsWith(route + '/');
  });
};

/**
 * Unified Authentication Hook
 *
 * Uses Firebase Auth for all authentication, providing a consistent API
 * across the entire application with multi-tenant support.
 *
 * Roles and permissions are fetched from the database RBAC system
 * via the /api/auth/profile endpoint.
 *
 * @returns {Object} Authentication state and methods
 */
export const useAuth = () => {
  const shared = useContext(AuthContext);
  if (shared) return shared;

  // No provider above this component. Fall back to a private subscription so
  // nothing hard-crashes, but say so loudly in development — the fallback
  // reintroduces the duplicate-listener problem AuthProvider exists to solve.
  if (import.meta.env.DEV) {
    console.warn(
      '[useAuth] No AuthProvider found above this component. Falling back to a ' +
      'private Firebase subscription, which means this component has its own ' +
      'auth state and can disagree with the rest of the tree. Mount ' +
      '<AuthProvider> in App.jsx above this subtree.'
    );
  }
  return useAuthState();
};

/**
 * The actual derivation. Called once by AuthProvider, or directly by useAuth as
 * a fallback when no provider is mounted.
 */
const useAuthState = () => {
  const firebaseAuth = useFirebaseAuth();

  // Determine if user is a super admin from database-backed claims
  // These are set by the backend via RBAC system
  const isSuperAdmin = firebaseAuth.user?.isSuperAdmin ||
                       firebaseAuth.user?.roles?.includes('super_admin');

  // Determine if user has admin access
  const isAdmin = isSuperAdmin ||
                  firebaseAuth.user?.isTenantAdmin ||
                  firebaseAuth.user?.roles?.includes('admin') ||
                  firebaseAuth.user?.roles?.includes('tenant_admin');

  return {
    ...firebaseAuth,
    // Auth system identifier
    authSystem: 'firebase',

    // Context flags
    isAdminContext: isAdmin,
    isCustomerContext: !isAdmin,
    isFirebaseContext: true,

    // Authentication state
    isAuthenticated: firebaseAuth.isSignedIn,
    isLoaded: !firebaseAuth.loading,

    // Admin role check (from database RBAC)
    isAdmin,
    isSuperAdmin,

    // Aliases for backward compatibility
    login: firebaseAuth.signInWithEmail,
    logout: firebaseAuth.signOut,
    register: firebaseAuth.signUpWithEmail,
    signIn: firebaseAuth.signInWithEmail,
    signUp: firebaseAuth.signUpWithEmail,
  };
};

// Alias for backward compatibility
export const useAuth$ = useAuth;

/**
 * Single source of auth state for the app.
 *
 * Was previously a no-op pass-through whose deprecation warning pointed at a
 * "FirebaseAuthProvider in App.jsx" that never existed — so nothing was mounted
 * and every consumer subscribed independently. Mount this once, above the
 * routes.
 */
export const AuthProvider = ({ children }) => {
  const value = useAuthState();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Helper to check if a given path requires admin access
 */
export { isAdminRoute };

export default useAuth;
